import type { BookingCategories } from '~common/storage/users'
import { SKR03_CATEGORIES } from './skr-03-categories'
import { SKR04_CATEGORIES } from './skr-04-categories'

export type TransactionCategoryType = 'EXPENSE' | 'REVENUE'

export interface SKRCategory {
    id: string
    label: string
    description: string
    icon?: string
    allowedTaxRules?: {
        name: string
        id: number
        description: string
        taxRates: string[]
    }[]
    allowedReceiptTypes?: string[]
}

function getDefaultIcon(type: TransactionCategoryType): string {
    return type === 'REVENUE' ? 'arrow-down' : 'arrow-up'
}

export function getAllCategories(
    bookingCategories?: BookingCategories,
): Record<string, SKRCategory> {
    const categoriesMap: Record<string, SKRCategory> =
        bookingCategories?.kontenrahmen === 'SKR03'
            ? SKR03_CATEGORIES
            : SKR04_CATEGORIES
    if (Object.keys(categoriesMap).length === 0) {
        return {}
    }

    return categoriesMap
}

export function getCategoriesFiltered(params: {
    transactionType?: TransactionCategoryType
    bookingCategories?: BookingCategories
    searchQuery?: string
    includeInactiveOnNoResults?: boolean
}): Record<string, SKRCategory> {
    const categories = getAllCategories(params.bookingCategories)

    const filterCategories = (includeActiveCheck: boolean) => {
        return Object.values(categories).filter((category) => {
            const searchMatch = params.searchQuery
                ? (() => {
                      const lowerQuery = params.searchQuery.trim().toLowerCase()
                      const tokens = lowerQuery
                          .split(/\s+/)
                          .filter((token) => token.length > 0)
                      return (
                          tokens.length === 0 ||
                          tokens.every((token) => {
                              if (/^\d+$/.test(token)) {
                                  return category.id.includes(token)
                              } else {
                                  return (
                                      category.label
                                          .toLowerCase()
                                          .includes(token) ||
                                      category.description
                                          .toLowerCase()
                                          .includes(token)
                                  )
                              }
                          })
                      )
                  })()
                : true

            const typeMatch = params.transactionType
                ? category.allowedReceiptTypes?.includes(
                      params.transactionType as string,
                  )
                : true

            const activeMatch =
                includeActiveCheck && params.bookingCategories?.activeCategories
                    ? params.bookingCategories.activeCategories.includes(
                          Number(category.id),
                      )
                    : true

            return searchMatch && typeMatch && activeMatch
        })
    }

    let filteredCategories = filterCategories(true)

    if (params.includeInactiveOnNoResults && filteredCategories.length === 0) {
        filteredCategories = filterCategories(false)
    }

    return Object.fromEntries(
        filteredCategories.map((category) => [
            category.id,
            {
                ...category,
                ...(params.transactionType
                    ? { icon: getDefaultIcon(params.transactionType) }
                    : {}),
            },
        ]),
    )
}

export function getCategoriesForLLM(
    transactionType: TransactionCategoryType,
    bookingCategories: BookingCategories,
): string {
    const categories = getCategoriesFiltered({
        transactionType,
        bookingCategories,
    })
    const slimmedDownCategories = Object.values(categories).map((category) => ({
        id: category.id,
        label: category.label,
        ...(category.description ? { info: category.description } : {}),
    }))
    return JSON.stringify(slimmedDownCategories)
}

export function resolveCategoryLabel(
    categoryId: number,
    bookingCategories?: BookingCategories,
): string {
    const categories = getAllCategories(bookingCategories)

    const category = categories[categoryId]

    return category ? `${category.id} - ${category.label}` : ''
}
